// ── MessageComposer ────────────────────────────────────────────────
//
// Two-mode composer:
//   • Reply        → sends a WhatsApp `text` message via Phase 4
//                    POST /whatsapp/messages
//   • Internal Note → POSTs to Phase 7 .../notes (never reaches Meta)
//
// Reply mode is disabled with a clear in-place explanation when:
//   • Session window is closed (24h rule) — for the MVP we don't have
//     a template picker yet, so we just explain what's needed.
//   • Conversation status is CLOSED or ARCHIVED.
//   • The account isn't CONNECTED.
//
// Send is optimistic — the caller (ChatWindow) receives the just-sent
// message id and the parent refetches on success. On failure, the
// composer keeps the draft so the user can retry.

import { useEffect, useRef, useState } from 'react';
import { Send, StickyNote, Loader2, AlertCircle, LayoutTemplate } from 'lucide-react';
import { addInternalNote, sendMessage } from '../api/whatsapp.api';
import type { ConversationDetailResponse } from '../api/whatsapp.api';
import { SessionBadge } from './SessionBadge';
import { TemplatePicker, type TemplatePickerResult } from './TemplatePicker';

type Mode = 'reply' | 'note';

// Meta allows up to 4096 chars for text messages. Use the same ceiling
// so the composer doesn't cut off valid messages.
const MAX_LEN = 4096;

// Build the template message content with variable substitution
function buildTemplateContent(
  template: { name: string; language: string; components: any[] },
  variableValues: Record<string, string>,
) {
  // Send as a template message type
  const components = template.components
    .filter((c) => c.type === 'BODY' && Object.keys(variableValues).length > 0)
    .map((c) => ({
      type: 'body',
      parameters: Object.entries(variableValues).map(([, value]) => ({
        type: 'text',
        text: value,
      })),
    }));
  return {
    name: template.name,
    language: template.language,
    components: components.length > 0 ? components : undefined,
  };
}

export function MessageComposer({
  conversation,
  onSent,
  onNoteAdded,
}: {
  conversation: ConversationDetailResponse;
  onSent: () => void;
  onNoteAdded: () => void;
}) {
  const [mode, setMode] = useState<Mode>('reply');
  const [text, setText] = useState('');
  const [sending, setSending] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [templatePickerOpen, setTemplatePickerOpen] = useState(false);
  const textareaRef = useRef<HTMLTextAreaElement>(null);

  // Disable reasons for reply mode.
  //
  // NOTE: a closed 24h session window NO LONGER disables the composer.
  // The agent can type and hit send — the backend (MessageSendService)
  // auto-converts the freeform reply into the configured re-engagement
  // template to re-open the window. If no auto-fallback template is
  // configured, the backend returns SESSION_CLOSED and we surface that
  // error so the agent can pick a template via "Send template".
  const replyDisabled = (() => {
    if (conversation.account?.status !== 'CONNECTED') {
      return 'WhatsApp account is not connected.';
    }
    if (conversation.status === 'CLOSED' || conversation.status === 'ARCHIVED') {
      return 'Conversation is closed. Reopen to send messages.';
    }
    return null;
  })();

  const sessionClosed =
    conversation.account?.status === 'CONNECTED' &&
    conversation.status !== 'CLOSED' &&
    conversation.status !== 'ARCHIVED' &&
    !conversation.sessionStatus.isOpen;

  const handleTemplateSend = async (result: TemplatePickerResult) => {
    setTemplatePickerOpen(false);
    setSending(true);
    setError(null);
    try {
      await sendMessage({
        whatsappAccountId: conversation.whatsappAccountId,
        toWaId: conversation.contactWaId,
        contactName: conversation.contactName ?? undefined,
        type: 'template',
        content: buildTemplateContent(result.template, result.variableValues),
        idempotencyKey: `tpl-${conversation.id}-${result.template.id}-${Date.now()}`,
      });
      onSent();
    } catch (e: any) {
      const msg =
        e?.response?.data?.message ??
        e?.response?.data?.error ??
        e?.message ??
        'Send failed';
      setError(typeof msg === 'string' ? msg : 'Send failed');
    } finally {
      setSending(false);
    }
  };

  const noteDisabled = sending;

  // Auto-grow textarea up to ~6 lines.
  useEffect(() => {
    const el = textareaRef.current;
    if (!el) return;
    el.style.height = 'auto';
    el.style.height = `${Math.min(el.scrollHeight, 6 * 24)}px`;
  }, [text]);

  // Reset on conversation switch.
  useEffect(() => {
    setText('');
    setError(null);
    setMode('reply');
  }, [conversation.id]);

  const submit = async () => {
    const body = text.trim();
    if (!body || sending) return;
    setError(null);
    setSending(true);
    try {
      if (mode === 'reply') {
        if (replyDisabled) {
          setError(replyDisabled);
          setSending(false);
          return;
        }
        await sendMessage({
          whatsappAccountId: conversation.whatsappAccountId,
          toWaId: conversation.contactWaId,
          contactName: conversation.contactName ?? undefined,
          type: 'text',
          content: { body, preview_url: true },
          // Client idempotency: one key per logical attempt.
          idempotencyKey: `compose-${conversation.id}-${Date.now()}`,
        });
        setText('');
        onSent();
      } else {
        await addInternalNote(conversation.id, body);
        setText('');
        onNoteAdded();
      }
    } catch (e: any) {
      const msg =
        e?.response?.data?.message ??
        e?.response?.data?.error ??
        e?.message ??
        'Send failed';
      setError(typeof msg === 'string' ? msg : 'Send failed');
    } finally {
      setSending(false);
    }
  };

  const onKeyDown = (e: React.KeyboardEvent<HTMLTextAreaElement>) => {
    // Enter sends, Shift+Enter newlines. Cmd/Ctrl+Enter also sends.
    if (e.key === 'Enter' && !e.shiftKey) {
      e.preventDefault();
      submit();
    } else if (e.key === 'Escape') {
      setText('');
      setError(null);
    }
  };

  const isReply = mode === 'reply';
  const disabledReason = isReply ? replyDisabled : null;

  return (
    <div className="border-t border-gray-100 bg-white">
      {/* Mode toggle */}
      <div className="flex items-center justify-between gap-2 px-3 pt-2">
        <div className="flex items-center gap-0.5 rounded-md bg-slate-100 p-0.5">
          <button
            onClick={() => setMode('reply')}
            className={`flex items-center gap-1 rounded-[5px] px-2.5 py-1 text-xs font-medium transition-colors ${
              isReply ? 'bg-white text-indigo-600 shadow-sm' : 'text-slate-500 hover:text-slate-800'
            }`}
            type="button"
          >
            <Send className="h-3 w-3" />
            Reply
          </button>
          <button
            onClick={() => setMode('note')}
            className={`flex items-center gap-1 rounded-[5px] px-2.5 py-1 text-xs font-medium transition-colors ${
              !isReply ? 'bg-white text-amber-600 shadow-sm' : 'text-slate-500 hover:text-slate-800'
            }`}
            type="button"
          >
            <StickyNote className="h-3 w-3" />
            Internal note
          </button>
        </div>
        {isReply && (
          <SessionBadge
            lastInboundAt={conversation.lastInboundAt}
            sessionExpiresAt={conversation.sessionExpiresAt}
          />
        )}
      </div>

      {/* Disabled / error explainer */}
      {(disabledReason || error) && (
        <div
          className={`mx-3 mt-2 flex items-start gap-2 rounded-md px-2.5 py-1.5 text-[12px] ${
            error
              ? 'bg-red-50 text-red-700'
              : 'bg-amber-50 text-amber-700'
          }`}
        >
          <AlertCircle className="mt-0.5 h-3.5 w-3.5 flex-shrink-0" />
          <span>{error ?? disabledReason}</span>
        </div>
      )}

      {/* Session-closed re-engagement hint (composer stays enabled) */}
      {isReply && sessionClosed && !disabledReason && !error && (
        <div className="mx-3 mt-2 flex items-start gap-2 rounded-md bg-amber-50 px-2.5 py-1.5 text-[12px] text-amber-700">
          <AlertCircle className="mt-0.5 h-3.5 w-3.5 flex-shrink-0" />
          <span>
            24h window is closed — your reply will be sent automatically as the re-engagement
            template. Or use “Send template” to pick a specific one.
          </span>
        </div>
      )}

      {/* Textarea + send */}
      <div className="flex items-end gap-2 px-3 py-2">
        <textarea
          ref={textareaRef}
          value={text}
          onChange={(e) => setText(e.target.value.slice(0, MAX_LEN))}
          onKeyDown={onKeyDown}
          placeholder={
            isReply
              ? disabledReason
                ? '— send disabled —'
                : sessionClosed
                  ? 'Type to re-engage… (sent as the re-engagement template)'
                  : 'Type a reply… (Enter to send, Shift+Enter for newline)'
              : 'Add an internal note for your team…'
          }
          disabled={isReply ? !!disabledReason : noteDisabled}
          className={`flex-1 resize-none rounded-md border bg-white px-3 py-2 text-[14px] leading-relaxed placeholder:text-slate-400 focus:outline-none focus:ring-2 disabled:cursor-not-allowed disabled:bg-slate-50 ${
            isReply
              ? 'border-gray-200 focus:border-indigo-300 focus:ring-indigo-100'
              : 'border-amber-200 focus:border-amber-300 focus:ring-amber-100'
          }`}
          rows={1}
        />
        <button
          onClick={submit}
          disabled={!text.trim() || sending || (isReply && !!disabledReason)}
          className={`flex-shrink-0 inline-flex h-9 items-center gap-1.5 rounded-md px-3 text-[13px] font-medium text-white transition-colors disabled:cursor-not-allowed disabled:bg-slate-300 ${
            isReply ? 'bg-indigo-500 hover:bg-indigo-600' : 'bg-amber-500 hover:bg-amber-600'
          }`}
          type="button"
          aria-label={isReply ? 'Send message' : 'Add note'}
        >
          {sending ? (
            <Loader2 className="h-4 w-4 animate-spin" />
          ) : (
            <Send className="h-3.5 w-3.5" />
          )}
          {isReply ? 'Send' : 'Save'}
        </button>
      </div>

      {/* Footer hint + template button */}
      <div className="flex items-center justify-between px-3 pb-2 text-[10.5px] text-slate-400">
        <span>
          {isReply
            ? 'Replies are sent to WhatsApp via Meta Cloud API.'
            : 'Notes are visible to your team only — never sent to WhatsApp.'}
        </span>
        <div className="flex items-center gap-2">
          {isReply && sessionClosed && (
            <button
              onClick={() => setTemplatePickerOpen(true)}
              disabled={sending}
              className="inline-flex items-center gap-1 rounded-md border border-indigo-200 bg-indigo-50 px-2 py-1 text-[11px] font-medium text-indigo-600 hover:bg-indigo-100 disabled:opacity-50"
              title="Send a template to re-open the session"
            >
              <LayoutTemplate className="h-3 w-3" />
              Send template
            </button>
          )}
          <span>{text.length} / {MAX_LEN}</span>
        </div>
      </div>

      {/* Template picker modal */}
      {templatePickerOpen && (
        <TemplatePicker
          whatsappAccountId={conversation.whatsappAccountId}
          onSelect={handleTemplateSend}
          onClose={() => setTemplatePickerOpen(false)}
        />
      )}
    </div>
  );
}
